export const BASE_MESSAGE_LENGTH = 160;
export const SMS_SEGMENT_CHARACTER_LIMIT = 153;
export const MMS_SEGMENT_CHARACTER_LIMIT = 1600;
export const BASE_SMS_COST = 0.035;
export const BASE_MMS_IMAGE_COST = 0.055;
export const BASE_MMS_VIDEO_COST = 0.055;

// Calls bill in whole five minute units, rounded up per call, minimum one unit.
export const SECONDS_PER_BILLED_CALL_UNIT = 300;
export const BASE_RECORDING_CALL_COST = 0.035; // per billed unit for TwiML recording calls
export const BASE_VOICE_CALL_COST = 0.05; // per billed unit for TwiML voice SDK calls

export const BASE_MMS_LENGTH = 1600;
export const SUCCESS_SMS_STATUSES = ['sent', 'delivered', 'scheduled'];
export const BILLING_SUCCESS_SMS_STATUSES = ['sent', 'delivered'];
export const SUCCESS_OR_CANCELED_SMS_STATUSES = [...SUCCESS_SMS_STATUSES, 'canceled'];
export const FAILED_SMS_STATUSES = ['undelivered', 'failed'];
export const FINISHED_SMS_STATUSES = [...SUCCESS_SMS_STATUSES, ...FAILED_SMS_STATUSES];
export const SENDING_SMS_STATUSES = ['sending', 'queued', 'accepted'];
export const BILLING_SENDING_SMS_STATUSES = ['sending', 'queued', 'accepted', 'scheduled'];
export const CANCELED_STATUS = 'canceled';
export const ALL_SMS_STATUSES = [
  ...FINISHED_SMS_STATUSES,
  ...SENDING_SMS_STATUSES,
  CANCELED_STATUS,
];
export const SUCCESS_CALL_STATUSES = ['completed'];
// Busy / no-answer calls were placed fine, the voter just didn't pick up — count them as
// submitted, not as errors.
export const UNANSWERED_CALL_STATUSES = ['busy', 'no-answer'];
export const FAILED_CALL_STATUSES = ['failed'];
export const FINISHED_CALL_STATUSES = [
  ...SUCCESS_CALL_STATUSES,
  ...UNANSWERED_CALL_STATUSES,
  ...FAILED_CALL_STATUSES,
];
export const PENDING_CALL_STATUSES = ['queued'];
export const ALL_CALL_STATUES = [...FINISHED_CALL_STATUSES, ...PENDING_CALL_STATUSES];

// CREATED in a seed state not part of DirecSND
export const CANCELED_DIRECT_SND_SMS_STATUS = 'CANCELED';
export const DIRECT_SND_SMS_SUBMITTED_STATUSES = ['CREATED', 'PENDING'];
// EXPIRED counts as a success: DirectSND handed the message to the carrier, which never reported
// back before the validity period ran out. DELIVERED stays first — billing assumes it via [0].
export const DIRECT_SND_SMS_SUCCESS_STATUSES = ['DELIVERED', 'EXPIRED'];
export const DIRECT_SND_SMS_FAILED_STATUSES = ['UNDELIVERABLE', 'REJECTED', 'failed'];
export const FINISHED_DIRECT_SND_SMS_STATUSES = [
  ...DIRECT_SND_SMS_SUCCESS_STATUSES,
  ...DIRECT_SND_SMS_FAILED_STATUSES,
];
export const ALL_DIRECT_SND_SMS_STATUSES = [
  ...DIRECT_SND_SMS_SUBMITTED_STATUSES,
  ...FINISHED_DIRECT_SND_SMS_STATUSES,
  CANCELED_DIRECT_SND_SMS_STATUS,
];

export const STARTED_SURVEY_STATUSES = ['started'];
export const SUCCESS_SURVEY_STATUSES = ['ended'];
export const FAILED_SURVEY_STATUSES = ['failed'];
export const SENT_SURVEY_STATUSES = [...STARTED_SURVEY_STATUSES, ...SUCCESS_SURVEY_STATUSES];
export const ALL_SURVEY_STATUSES = [...SENT_SURVEY_STATUSES, ...FAILED_SURVEY_STATUSES];

// Only allow images and video
export const VALID_IMAGE_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
export const VALID_VIDEO_FILE_TYPES = ['video/mpeg', 'video/mp4', 'video/quicktime', 'video/webm'];
// Text-message tasks take MP4 and 3GP video only.
export const TASK_VIDEO_FILE_TYPES = ['video/mp4', 'video/3gpp'];
export const VALID_AUDIO_FILE_TYPES = [
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/wave',
  'audio/x',
  'audio/x-m4a',
  'audio/aiff',
  'audio/gsm',
  'audio/ulaw',
];

export const TRANSCODE_AUDIO_FILE_TYPES = ['audio/x-m4a'];

// SMS/MMS attachments are capped at 1MB wherever they are attached — texting task, manual
// text batch, quick text, message template, or reply. That matches the cap enforced by the
// upload endpoint in backend/src/services/twilio/media-upload-handler.ts; change them
// together. Voice-recording audio uses the same upload endpoint but is a different asset
// class and keeps its own cap.
export const MAX_SMS_MEDIA_BYTES = 1024 * 1024;
export const MAX_SMS_MEDIA_LABEL = '1MB';
// Texting tasks are held to a tighter cap than the other surfaces.
export const MAX_TASK_SMS_MEDIA_BYTES = 750 * 1024;
export const MAX_TASK_SMS_MEDIA_LABEL = '750KB';
export const MAX_AUDIO_MEDIA_BYTES = 2048 * 1024;
export const MAX_AUDIO_MEDIA_LABEL = '2MB';
export const MAX_SMS_MEDIA_ERROR = `File size should be less than ${MAX_SMS_MEDIA_LABEL}`;
export const MAX_AUDIO_MEDIA_ERROR = `File size should be less than ${MAX_AUDIO_MEDIA_LABEL}`;

export const TWILIO_UNSUBSCRIBED_ERROR_CODE = 21610;

export const DIRECT_SND_UNSUBSCRIBED_ERROR = 'UNSUB';

export const CALLS_PER_SECOND = 1; // CPS - average calls per second throughput set in the admin panel

export const TEXT_STOP_TO_END_MESSAGE_SUFFIX = `\nText STOP to End`;

// SURVEY_LINK_BASE_URL in backend/src/utils/survey-link-token.ts, so the authoring estimate
// matches what the send path builds.
export const SURVEY_LINK_BASE_URL = 'https://txtpoll.org/';

// SURVEY_LINK_CODE_LENGTH in backend/src/utils/survey-link-code.ts: every text carries a 7-character
// short code. Only if a batch cannot store its codes does it fall back to the 20-22 character signed
// token (logged), so those messages alone may run a segment over this estimate.
export const SURVEY_LINK_TOKEN_MAX_LENGTH = 7;

// The blank line before the link, plus the link.
export const SURVEY_LINK_SMS_LENGTH =
  2 + SURVEY_LINK_BASE_URL.length + SURVEY_LINK_TOKEN_MAX_LENGTH;

export const TEXTING_TASK_FORM_VALUES_LOCAL_STORAGE_KEY =
  'TEXTING_TASK_FORM_VALUES_LOCAL_STORAGE_KEY';

export const POWER_DIALER_STATES = ['CA'];

export const SURVEY_COST_FIRST_QUESTION = 6;
export const SURVEY_COST_SUBSEQUENT_QUESTION = 2;

export const STRIPE_INVOICE_STATUSES = {
  DRAFT: 'draft',
  OPEN: 'open',
  OVERDUE: 'overdue', // custom status does not exist in stripe
  PAID: 'paid',
  REFUNDED: 'refunded', // custom status does not exist in stripe
  UNCOLLECTIBLE: 'uncollectible',
  VOID: 'void',
};

export const REASON_INVOICE_STATUSES = {
  NO_INVOICE: 'no_invoice',
  OPEN: 'manually_paid',
  PAID: 'paid',
};

export const STRIPE_INVOICE_STATUSE_LABELS = {
  draft: 'Draft',
  open: 'Open',
  overdue: 'Overdue', // custom status does not exist in stripe
  paid: 'Paid',
  refunded: 'Refunded', // custom status does not exist in stripe
  uncollectible: 'Uncollectible',
  void: 'Void',
};

export const STRIPE_TRANSACTION_STATUSES = {
  CANCELED: 'canceled',
  PROCESSING: 'processing',
  REQUIRES_ACTION: 'requires_action',
  REQUIRES_CAPTURE: 'requires_capture',
  REQUIRES_CONFIRMATION: 'requires_confirmation',
  REQUIRES_PAYMENT_METHOD: 'requires_payment_method',
  SUCCEEDED: 'succeeded',
};

export const STRIPE_TRANSACTION_STATUSES_LABELS = {
  canceled: 'Canceled',
  processing: 'Processing',
  requires_action: 'Requires Action',
  requires_capture: 'Requires Capture',
  requires_confirmation: 'Requires Confirmation',
  requires_payment_method: 'Requires Payment Method',
  succeeded: 'Paid',
};

/**
 * Where voice-task audio is stored.
 *
 * Deliberately NOT the default twilio-media bucket: that one carries a
 * `DeleteOldMedia` lifecycle rule expiring every object after 14 days, which
 * silently deletes campaign voicemail while tasks still point at it. Twilio's
 * <Play> then fetches a dead URL, gets S3's XML error document, and speaks
 * "an application error has occurred" to the voter instead of the recording.
 *
 * permanent-media-bucket has no lifecycle rule and grants public s3:GetObject,
 * which is what Twilio needs to fetch the file.
 */
export const VOICE_TASK_MEDIA_TARGET = {
  bucket: 'permanent-media-bucket',
  folder: 'outgoing-calls',
};

export const TWILIO_VOICE_WEBHOOK_URL =
  'https://bq1o18bczk.execute-api.us-west-1.amazonaws.com/dev/twilio/voice-webhook';

export const TWILIO_CALL_STATUSES = {
  QUEUED: 'queued',
  INITIATED: 'initiated',
  RINGING: 'ringing',
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed',
  BUSY: 'busy',
  FAILED: 'failed',
  NO_ANSWER: 'no-answer',
};

export const TASK_BILLING_STATUS_CODES = {
  REQUIRED: 'required',
  SUCCESS: 'success',
  NOT_APPLICABLE: 'N/A',
};

export const TASK_BILLING_STATUS_ID_MAP = {
  [TASK_BILLING_STATUS_CODES.REQUIRED]: 1,
  [TASK_BILLING_STATUS_CODES.SUCCESS]: 2,
  [TASK_BILLING_STATUS_CODES.NOT_APPLICABLE]: 3,
};

export const MACHINE_API_ENABLED_ORG_IDS = [12343];

export const ORG_SMS_PROVIDERS = {
  DIRECT_SND: 'direct_snd',
  TWILIO: 'twilio',
};

export const ORG_SMS_PROVIDERS_OPTIONS = [
  {
    label: 'Direct SND',
    value: ORG_SMS_PROVIDERS.DIRECT_SND,
  },
  {
    label: 'Twilio',
    value: ORG_SMS_PROVIDERS.TWILIO,
  },
];

// Master switch for web calling — Assigned Tasks, and the volunteer access it brought.
//
// On: a volunteer can sign into the web portal if at least one of the organizations they
// volunteer in is in WEB_CALLING_ENABLED_ORG_IDS below (otherwise they get the app-download page,
// as with the switch off), and Assigned Tasks is available to the listed organizations.
// Off: the web portal is the manager surface again. Only organizations the user manages (by org
// role) are offered (webEligibleMemberships, src/utils/roles.js); a user who only volunteers is
// signed in but kept on the app-download page, paths.volunteers.root (isAppOnlyUser); and
// Assigned Tasks is off for every organization, whatever the list says.
// Platform-wide roles (superadmin, legislative member) are unaffected either way.
export const WEB_CALLING_ENABLED = false;

// Organizations that can use Assigned Tasks — calling and power-dialing tasks in the browser —
// while WEB_CALLING_ENABLED is on. The per-org control on web calling, and the volunteer sign-in
// gate: a volunteer gets into the web portal only through a listed org (isAppOnlyUser,
// src/utils/roles.js). For an org not listed here the nav item is hidden, volunteers who got in
// through another org cannot navigate to Assigned Tasks (EffectiveRoleGuard sends them to voter
// search), and a manager who reaches it meets a full-page "not enabled" card
// (layouts/dashboard/main.js, like the expired-membership lock); the call session redirects to it.
// Web-only: nothing server-side reads this list. Calling on the mobile app is unaffected.
//
// Empty turns Assigned Tasks off for every organization.
export const WEB_CALLING_ENABLED_ORG_IDS = [1288, 18318];

// Organizations whose managers can put a per-recipient survey link on a text message they are
// creating. A superadmin can for any organization. Web-only, like the rest of this feature:
// attaching a question bank to a texting task is what turns links on at send time. Changing or
// removing it afterwards, in the task settings dialog, stays superadmin-only.
export const SURVEY_LINK_ENABLED_ORG_IDS = [12838];

export const canSendSurveyLinks = (isSuperAdmin, organizationId) =>
  isSuperAdmin || SURVEY_LINK_ENABLED_ORG_IDS.includes(Number(organizationId));

// Twilio `AnsweredBy` values that mean a machine picked up. Mirrors the backend's own
// `AnsweredBy.includes('machine')` check and the app's `ANSWERING_MACHINE_ANSWERED_BY_VALUES`
// (`right-impact-app/src/utils/constants/twilio.ts`) — see docs/power-dialer.md §4.3 for why an
// explicit list and a substring check never actually disagree here.
export const ANSWERING_MACHINE_ANSWERED_BY_VALUES = [
  'machine_end_beep',
  'machine_end_silence',
  'machine_end_other',
];

// The store listings, shared by the marketing download section and the public volunteer signup
// page's success card, which needs them without the rest of that section.
export const APP_STORE_URL = 'https://apps.apple.com/us/app/right-impact/id6448124742';
export const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.rightinsight.android';
